import { CircleHelp, Mars, Venus } from 'lucide-react'

import type { PatientSex, PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { RadioGroup, RadioOption } from '@/customer/components/ui/radio-group'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'

const SEX_LABELS: Record<PatientSex, string> = {
  male: 'Male',
  female: 'Female',
  unknown: 'Not sure',
}

/**
 * Sex row. Read-only once the pet has clinical activity, for the same
 * reason as species. Label icon tracks the current sex so the row
 * identity matches the pet-preview-card idiom.
 */
export function SexPetField({
  pet,
  hasClinicalActivity,
}: {
  pet: PublicPatient
  hasClinicalActivity: boolean
}) {
  const updatePet = useUpdatePet(pet.id)
  const sexField = useEditableField<PatientSex>({
    initialValue: pet.sex,
    onSave: async ({ value, setError }) => {
      if (value === pet.sex) return
      try {
        await updatePet.mutateAsync({ sex: value })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  const icon = pet.sex === 'female' ? Venus : pet.sex === 'male' ? Mars : CircleHelp

  return (
    <EditableField
      label="Sex"
      icon={icon}
      error={sexField.error}
      action={hasClinicalActivity ? undefined : <EditButton field={sexField} label="sex" />}
    >
      {sexField.isEditing ? (
        <RadioGroup
          value={sexField.draft}
          onValueChange={(v) => sexField.setDraft(v as PatientSex)}
        >
          <RadioOption value="male">Male</RadioOption>
          <RadioOption value="female">Female</RadioOption>
          <RadioOption value="unknown">Not sure</RadioOption>
        </RadioGroup>
      ) : (
        <EditableValue>{SEX_LABELS[pet.sex]}</EditableValue>
      )}
      <EditActions field={sexField} />
    </EditableField>
  )
}
